import {
  startTransition,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router";

import {
  buildAuditLogChangedFilterTelemetryContext,
  buildAuditLogFilterTelemetryContext,
  resolveAuditLogPageViewState,
} from "@/pages/AdminV2/auditLogPageHelpers";
import {
  buildAuditLogFilterValuesFromSearchParams,
  buildAuditLogQueryArgs,
  buildAuditLogSearchParams,
} from "@/pages/AdminV2/auditLogUtils";
import { useGetAdminV2AuditLogQuery } from "@/store/api/baseApi";
import type { AdminV2AuditLogSafeDataSummary } from "@/types/adminV2";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  VNextButton,
  VNextCard,
  VNextInput,
  VNextNotice,
} from "@/vnext/primitives";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";
import {
  formatVNextDateTime,
  formatVNextLabel,
} from "@/vnext/shared/formatters";
import { useDebouncedValue } from "@/vnext/shared/useDebouncedValue";

const logger = FrontendLoggerFactory.getLogger().withComponent("AuditLogPage");

const hasAuditLogDetail = (
  entry: NonNullable<
    ReturnType<typeof useGetAdminV2AuditLogQuery>["data"]
  >["entries"][number],
): boolean =>
  [
    entry.beforeStateSummary.hasPrivateDetails,
    entry.afterStateSummary.hasPrivateDetails,
    entry.metadataSummary.hasPrivateDetails,
  ].some(Boolean);

const formatSummaryCount = (value: number | null): string =>
  value === null ? "Not applicable" : String(value);

const UNSAFE_SUMMARY_FIELD_VALUE_PATTERN =
  /auth|authorization|bearer|body|cookie|customer|email|export|file|html|jwt|oauth|password|payload|phone|private|provider|raw|secret|select|sql|stack|token|trace|url|uri/i;
const JSON_WEB_TOKEN_PATTERN =
  /^[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}$/;

const isSafeSummaryFieldValue = (value: string): boolean => {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0 || trimmedValue.length > 80) {
    return false;
  }

  return (
    /^[a-z0-9_.:-]+$/i.test(trimmedValue) &&
    !UNSAFE_SUMMARY_FIELD_VALUE_PATTERN.test(trimmedValue) &&
    !JSON_WEB_TOKEN_PATTERN.test(trimmedValue)
  );
};

const renderSafeAuditSummary = (
  title: string,
  summary: AdminV2AuditLogSafeDataSummary,
) => {
  const displayableSafeFields = summary.safeFields.filter((field) =>
    isSafeSummaryFieldValue(field.value),
  );

  return (
    <div className="vnext-field">
      <span className="vnext-field__label">{title}</span>
      <p className="vnext-muted">{summary.message}</p>
      <div className="vnext-detail-grid">
        <div className="vnext-field">
          <span className="vnext-field__label">Data type</span>
          <strong>{formatVNextLabel(summary.valueType)}</strong>
        </div>
        <div className="vnext-field">
          <span className="vnext-field__label">Fields</span>
          <strong>{formatSummaryCount(summary.fieldCount)}</strong>
        </div>
        <div className="vnext-field">
          <span className="vnext-field__label">Items</span>
          <strong>{formatSummaryCount(summary.itemCount)}</strong>
        </div>
        <div className="vnext-field">
          <span className="vnext-field__label">Values</span>
          <strong>{summary.scalarCount}</strong>
        </div>
      </div>
      {displayableSafeFields.length > 0 ? (
        <ul className="vnext-list">
          {displayableSafeFields.map((field) => (
            <li key={field.key}>
              <strong>{`${field.label} (${field.value})`}</strong>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default function AuditLogRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigationToken = useId();
  const navigationId = `audit-log-${navigationToken.replace(/:/g, "")}`;
  const pageStartedAtRef = useRef<number | null>(null);
  const readyLoggedRef = useRef(false);
  const filters = useMemo(
    () => buildAuditLogFilterValuesFromSearchParams(searchParams),
    [searchParams],
  );
  const [filterResetVersion, setFilterResetVersion] = useState(0);
  const queryFilters = useDebouncedValue(
    filters,
    undefined,
    filterResetVersion,
  );
  const hasPendingTextFilters =
    JSON.stringify(filters) !== JSON.stringify(queryFilters);
  const queryArgs = useMemo(
    () =>
      buildAuditLogQueryArgs({
        organisationId: queryFilters.organisationId,
        actor: queryFilters.actor,
        target: queryFilters.target,
        action: queryFilters.action,
        fromDate: queryFilters.fromDate,
        toDate: queryFilters.toDate,
      }),
    [
      queryFilters.action,
      queryFilters.actor,
      queryFilters.fromDate,
      queryFilters.organisationId,
      queryFilters.target,
      queryFilters.toDate,
    ],
  );
  const query = useGetAdminV2AuditLogQuery(queryArgs);
  const entries = useMemo(
    () => query.data?.entries ?? [],
    [query.data?.entries],
  );
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const rawFilterArgs = useMemo(
    () =>
      buildAuditLogQueryArgs({
        organisationId: filters.organisationId,
        actor: filters.actor,
        target: filters.target,
        action: filters.action,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      }),
    [
      filters.action,
      filters.actor,
      filters.fromDate,
      filters.organisationId,
      filters.target,
      filters.toDate,
    ],
  );
  const hasActiveFilters = Object.keys(rawFilterArgs).length > 1;
  const pageViewState = resolveAuditLogPageViewState({
    entryCount: entries.length,
    isError: query.isError,
    isLoading: query.isLoading,
  });
  const emptyStateMessage = hasActiveFilters
    ? "No audit log entries matched these filters. Clear the filters or widen the date range."
    : "No audit log entries are available yet.";
  const activeEntryId =
    selectedEntryId !== null &&
    entries.some((entry) => entry.id === selectedEntryId)
      ? selectedEntryId
      : entries.length > 0
        ? entries[0].id
        : null;
  const selectedEntry = useMemo(
    () =>
      activeEntryId === null
        ? null
        : (entries.find((entry) => entry.id === activeEntryId) ?? null),
    [activeEntryId, entries],
  );

  useEffect(() => {
    logger.debug("AUDIT_LOG_PAGE_ENTER", {
      ...buildAuditLogFilterTelemetryContext({
        organisationId: filters.organisationId,
        action: filters.action,
        actor: filters.actor,
        fromDate: filters.fromDate,
        target: filters.target,
        toDate: filters.toDate,
      }),
      component: "AuditLogPage",
      feature: "ADMIN",
      navigationId,
      operation: "AUDIT_LOG_PAGE_ENTER",
    });
    pageStartedAtRef.current = Date.now();
    readyLoggedRef.current = false;
  }, [
    filters.action,
    filters.actor,
    filters.fromDate,
    filters.organisationId,
    filters.target,
    filters.toDate,
    navigationId,
  ]);

  useEffect(() => {
    if (readyLoggedRef.current || query.isLoading) {
      return;
    }

    readyLoggedRef.current = true;
    logger.debug("AUDIT_LOG_PAGE_READY", {
      component: "AuditLogPage",
      feature: "ADMIN",
      navigationId,
      operation: "AUDIT_LOG_PAGE_READY",
      pageLoadDurationMs:
        pageStartedAtRef.current === null
          ? null
          : Date.now() - pageStartedAtRef.current,
      resultCount: entries.length,
    });
  }, [entries.length, navigationId, query.isLoading]);

  useEffect(() => {
    if (!query.isError) {
      return;
    }

    logger.error(
      "AUDIT_LOG_PAGE_FAIL",
      new Error("audit_log_page_load_failed"),
      {
        ...buildAuditLogFilterTelemetryContext({
          organisationId: filters.organisationId,
          action: filters.action,
          actor: filters.actor,
          fromDate: filters.fromDate,
          target: filters.target,
          toDate: filters.toDate,
        }),
        component: "AuditLogPage",
        feature: "ADMIN",
        navigationId,
        operation: "AUDIT_LOG_PAGE_FAIL",
      },
    );
  }, [
    filters.action,
    filters.actor,
    filters.fromDate,
    filters.organisationId,
    filters.target,
    filters.toDate,
    navigationId,
    query.isError,
  ]);

  const trackFilterChange = (
    filter:
      "action" | "actor" | "fromDate" | "organisationId" | "target" | "toDate",
    value: string,
  ) => {
    logger.debug("AUDIT_LOG_FILTER_CHANGED", {
      ...buildAuditLogFilterTelemetryContext({
        organisationId: filters.organisationId,
        action: filters.action,
        actor: filters.actor,
        fromDate: filters.fromDate,
        target: filters.target,
        toDate: filters.toDate,
      }),
      ...buildAuditLogChangedFilterTelemetryContext(filter, value),
      component: "AuditLogPage",
      feature: "ADMIN",
      navigationId,
      operation: "AUDIT_LOG_FILTER_CHANGED",
      source: "user",
    });
  };

  const updateFilter = (
    filter:
      "action" | "actor" | "fromDate" | "organisationId" | "target" | "toDate",
    value: string,
  ) => {
    trackFilterChange(filter, value);
    setSearchParams(
      buildAuditLogSearchParams({
        ...filters,
        [filter]: value,
      }),
      { replace: true },
    );
  };

  const resetFilters = () => {
    if (!hasActiveFilters) {
      return;
    }

    logger.debug("AUDIT_LOG_FILTERS_RESET", {
      ...buildAuditLogFilterTelemetryContext({
        organisationId: filters.organisationId,
        action: filters.action,
        actor: filters.actor,
        fromDate: filters.fromDate,
        target: filters.target,
        toDate: filters.toDate,
      }),
      component: "AuditLogPage",
      feature: "ADMIN",
      navigationId,
      operation: "AUDIT_LOG_FILTERS_RESET",
      source: "user",
    });
    // React Router applies search-param navigation in a transition; reset in
    // the same transition so no render queries the cleared filters.
    startTransition(() => {
      setFilterResetVersion((version) => version + 1);
      setSearchParams(new URLSearchParams(), { replace: true });
    });
  };

  return (
    <SettingsWorkspaceChrome
      actions={
        <VNextButton onClick={() => void query.refetch()} tone="secondary">
          Refresh
        </VNextButton>
      }
      subtitle="Inspect privileged action history and correlation records."
      title="Audit log"
    >
      <div className="vnext-stack">
        <VNextCard
          actions={
            <VNextButton
              disabled={!hasActiveFilters}
              onClick={resetFilters}
              tone="ghost"
            >
              Clear filters
            </VNextButton>
          }
          subtitle="Leave filters blank for the latest activity. Use Action when you know the exact event name."
          title="Filters"
        >
          <VNextNotice tone="info">
            Text filters update after you stop typing.
          </VNextNotice>
          {hasPendingTextFilters || (query.isFetching && !query.isLoading) ? (
            <VNextNotice role="status" tone="info">
              {hasPendingTextFilters
                ? "Waiting for you to stop typing before updating entries."
                : "Updating entries."}
            </VNextNotice>
          ) : null}
          <div className="vnext-grid vnext-grid--two">
            <label className="vnext-field">
              <span className="vnext-field__label">Organisation ID</span>
              <VNextInput
                onChange={(event) =>
                  updateFilter("organisationId", event.target.value)
                }
                placeholder="Tenant UUID"
                value={filters.organisationId}
              />
            </label>
            <label className="vnext-field">
              <span className="vnext-field__label">Actor</span>
              <VNextInput
                onChange={(event) => updateFilter("actor", event.target.value)}
                placeholder="Name, email, or Firebase UID"
                value={filters.actor}
              />
            </label>
            <label className="vnext-field">
              <span className="vnext-field__label">Target</span>
              <VNextInput
                onChange={(event) => updateFilter("target", event.target.value)}
                placeholder="Resource type or record ID"
                value={filters.target}
              />
            </label>
            <label className="vnext-field">
              <span className="vnext-field__label">Action</span>
              <VNextInput
                onChange={(event) => updateFilter("action", event.target.value)}
                placeholder="e.g. integrations.connection.updated"
                value={filters.action}
              />
            </label>
            <label className="vnext-field">
              <span className="vnext-field__label">Start time (UTC, ISO)</span>
              <VNextInput
                onChange={(event) =>
                  updateFilter("fromDate", event.target.value)
                }
                placeholder="2026-03-20T00:00:00Z"
                value={filters.fromDate}
              />
            </label>
            <label className="vnext-field">
              <span className="vnext-field__label">End time (UTC, ISO)</span>
              <VNextInput
                onChange={(event) => updateFilter("toDate", event.target.value)}
                placeholder="2026-03-20T23:59:59Z"
                value={filters.toDate}
              />
            </label>
          </div>
        </VNextCard>

        <VNextCard title="Entries">
          {pageViewState === "loading" ? (
            <div className="vnext-empty">Loading audit log…</div>
          ) : pageViewState === "error" ? (
            <VNextNotice tone="danger">
              Audit log entries could not be loaded.
            </VNextNotice>
          ) : pageViewState === "empty" ? (
            <div className="vnext-empty">{emptyStateMessage}</div>
          ) : (
            <div className="vnext-scroll-panel">
              <table className="vnext-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Tenant</th>
                    <th>Target</th>
                    <th>Occurred</th>
                    <th>Correlation</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      className={
                        entry.id === activeEntryId
                          ? "vnext-table__row--selected"
                          : undefined
                      }
                      key={entry.id}
                      onClick={() => setSelectedEntryId(entry.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedEntryId(entry.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <td>{entry.action}</td>
                      <td>
                        {entry.actorDisplayName ??
                          entry.actorEmail ??
                          entry.actorFirebaseUid ??
                          entry.actorStaffUserId}
                      </td>
                      <td>{entry.organisationId ?? "Global"}</td>
                      <td>
                        {entry.targetType ?? "Unknown"} {entry.targetId ?? ""}
                      </td>
                      <td>{formatVNextDateTime(entry.occurredAt)}</td>
                      <td>{entry.correlationId ?? "—"}</td>
                      <td>
                        {hasAuditLogDetail(entry)
                          ? "View detail"
                          : "View entry"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VNextCard>

        {selectedEntry !== null ? (
          <VNextCard title="Selected entry detail">
            <div className="vnext-detail-grid">
              <div className="vnext-field">
                <span className="vnext-field__label">Actor</span>
                <strong>
                  {selectedEntry.actorDisplayName ??
                    selectedEntry.actorEmail ??
                    selectedEntry.actorFirebaseUid ??
                    selectedEntry.actorStaffUserId}
                </strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Tenant</span>
                <strong>{selectedEntry.organisationId ?? "Global"}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Target</span>
                <strong>
                  {selectedEntry.targetType ?? "Unknown"}{" "}
                  {selectedEntry.targetId ?? ""}
                </strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Occurred</span>
                <strong>{formatVNextDateTime(selectedEntry.occurredAt)}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Correlation</span>
                <strong>
                  {selectedEntry.correlationId ?? "No correlation id"}
                </strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Source</span>
                <strong>{selectedEntry.source}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Entry hash</span>
                <strong>{selectedEntry.entryHash}</strong>
              </div>
              <div className="vnext-field">
                <span className="vnext-field__label">Previous hash</span>
                <strong>
                  {selectedEntry.previousHash ?? "No previous hash"}
                </strong>
              </div>
            </div>

            <div className="vnext-detail-stack">
              {renderSafeAuditSummary(
                "Before state summary",
                selectedEntry.beforeStateSummary,
              )}
              {renderSafeAuditSummary(
                "After state summary",
                selectedEntry.afterStateSummary,
              )}
              {renderSafeAuditSummary(
                "Metadata summary",
                selectedEntry.metadataSummary,
              )}
            </div>
          </VNextCard>
        ) : null}
      </div>
    </SettingsWorkspaceChrome>
  );
}
